"use client";

import { IconChevronDown } from "@tabler/icons-react";
import Link from "next/link";
import { FooterLinkItem } from "./footerItems";
import { Typography } from "@/components/Typography";
import { ArrowUpRight } from "lucide-react";
import { useSetAtom } from "jotai";
import { dialogAtom } from "@/stores/dialogAtom";

type FooterSectionProps = {
  title: string;
  items: FooterLinkItem[];
};

const isExternalLink = (href: string) => /^https?:\/\//i.test(href);

export const FooterSection = ({ title, items }: FooterSectionProps) => {
  const setDialog = useSetAtom(dialogAtom);

  return (
    <>
      <details className="group border-b pb-5 md:hidden">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-1">
          <Typography variant="subtitle2">{title}</Typography>
          <IconChevronDown className="size-5 text-heading transition-transform group-open:rotate-180" />
        </summary>
        <ul className="space-y-2 mt-2">
          {items.map((item) => (
            <li
              key={item.label}
              className={`text-[14px] leading-6 text-heading hover:underline ${item.underline ? "underline" : ""}`}
            >
              {item.action === "support_dialog" || item.label === "Help Center" ? (
                <button
                  type="button"
                  onClick={() => setDialog({ id: "support_dialog" })}
                  className="transition text-heading text-left cursor-pointer"
                >
                  {item.label}
                </button>
              ) : item.href ? (
                isExternalLink(item.href) ? (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition text-heading"
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link href={item.href} className="transition text-heading">
                    {item.label}
                  </Link>
                )
              ) : (
                item.label
              )}
            </li>
          ))}
        </ul>
      </details>

      <div className="hidden flex-col gap-2 md:flex">
        <Typography variant="subtitle2">{title}</Typography>
        <ul className="space-y-2">
          {items.map((item) => (
            <li
              key={item.label}
              className={`whitespace-nowrap text-[14px] leading-6 text-body hover:text-heading hover:underline ${item.underline ? "underline" : ""}`}
            >
              {item.action === "support_dialog" || item.label === "Help Center" ? (
                <button
                  type="button"
                  onClick={() => setDialog({ id: "support_dialog" })}
                  className="transition hover:text-heading text-body text-left cursor-pointer"
                >
                  {item.label}
                </button>
              ) : item.href ? (
                isExternalLink(item.href) ? (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 transition hover:text-heading text-body"
                  >
                    {item.label}
                    {item.icon === "arrow-up-right" && (
                      <ArrowUpRight size={14} strokeWidth={1.8} />
                    )}
                  </a>
                ) : (
                  <Link
                    href={item.href}
                    className="transition hover:text-heading text-body"
                  >
                    {item.label}
                  </Link>
                )
              ) : (
                item.label
              )}
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};
